import EditableDropdown from '../../../../../../common/EditableDropdown/EditableDropdown';
import ThreeDotMenu from '../../../../../../common/ThreeDotMenu/ThreeDotMenu';
import CtaPill from '../../../../../../common/_custom/CtaPill/CtaPill';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import UploadData from '../../../../../Company/UploadData/UploadData';
import AuditedStatementSection from '../../../AuditedFinancialsVisualisation/components/AuditedStatementSection/AuditedStatementSection';
import ProvisionalOtherMisSection from './ProvisionalOtherMisSection';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';

import { PROVISIONAL_STATEMENT_FALLBACK_LABELS } from './provisionalStatementTypes';
import { useProvisionalFinancialsLogic } from './useProvisionalFinancialsLogic';
import {
    AUDITED_UNIT_DEFAULT,
    AUDITED_UNIT_OPTIONS,
    FINANCIAL_YEAR_COLUMN_LABEL,
    CASH_FLOW_INDIRECT_DISCLOSURE,
} from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

import styles from './ProvisionalFinancials.module.scss';
import editClassificationIcon from '../../../AuditedFinancialsVisualisation/editClassificationIcon.svg';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { ctaPurpleRight } from '../../../../../../assets/hostedassets';

const UPLOAD_DATA_ICON = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/UploadDataInvestorProv.svg';

/**
 * Provisional Financials — stacked statement sections (RECUR-27640).
 *
 * Structured to match the redesigned Audited Financials page: a header carrying the title and the
 * units scale, then one collapsible section per statement on a single scrolling page. The three
 * statutory statements render through the SAME `AuditedStatementSection` the audited page uses,
 * unmodified, so the row treatments, the pinned Classification/years header and the empty-statement
 * state are identical by construction rather than by imitation.
 *
 * 'Other Metric (MIS)' is the exception and keeps `RawTable` — its payload is raw ledger rows for a
 * single classification, which a layout join by `classificationName` cannot represent. See
 * ProvisionalOtherMisSection for the full reasoning.
 *
 * Classification editing: this surface is read-only and shows the same DISABLED "Edit Classification"
 * placeholder Audited shows, because "consistent with Audited" means matching that control's
 * presence, not deleting the affordance. The write path (`saveClassification` →
 * `updateAuditedTableData`) is not wired here; re-homing it is tracked on the ticket alongside the
 * audited-side replacement.
 *
 * The metric dropdown is gone because the statements are now their own section headers, which is
 * what replaced it — a single statement is no longer selected.
 *
 * State, effects and data fetching live in `useProvisionalFinancialsLogic` per CLAUDE.md.
 */
interface ProvisionalFinancialsProps {
    /** RECUR-28096 — true only for the legacy single-metric PDF report route; suppresses the
     *  financials ingestion poll there (no such surface exists for provisional data today). */
    isPDFReport?: boolean;
}

const ProvisionalFinancials = ({ isPDFReport = false }: ProvisionalFinancialsProps) => {
    const {
        orgId,
        isMobile,
        canUploadData,
        unit,
        setUnit,
        resolvedMetrics,
        statutorySections,
        otherMisData,
        rawOptions,
        dataFormatOptions,
        isLoading,
        showUpload,
        setShowUpload,
        showFinancialMetrics,
        inProgress,
        headerRef,
        collapseActions,
        hideTitle,
        viewer,
    } = useProvisionalFinancialsLogic({ isPDFReport });

    // Shared by both collapse points below so they can never drift on what the menu contains.
    const kebabMenuItems = [
        { name: 'Financial Ratios', onClick: () => showFinancialMetrics() },
        { name: 'Edit Classification', onClick: () => {}, disabled: true },
        ...(canUploadData ? [{ name: 'Upload Data', onClick: () => setShowUpload(true) }] : []),
    ];
    const kebabMenu = <ThreeDotMenu color="var(--primary-purple)" menuItems={kebabMenuItems} />;

    return (
        <div>
            <div className={styles.HeadingContainer} ref={headerRef}>
                <div className={styles.TitleGroup}>
                    {!hideTitle && (
                        <>
                            <h3>Provisional Statements</h3>
                            <div className={styles.divider} />
                        </>
                    )}
                    <div className={styles.UnitsSelect}>
                        <span className={styles.UnitsLabel}>Units ₹:</span>
                        <EditableDropdown
                            id="ProvisionalUnitsSelector"
                            value={unit}
                            options={AUDITED_UNIT_OPTIONS}
                            setOutput={(val: string) => setUnit(val)}
                            removeInput={true}
                            optionStyle={{ fontSize: '14px' }}
                            placeHolder={AUDITED_UNIT_DEFAULT}
                        />
                    </div>
                </div>
                {isMobile && kebabMenu}
            </div>
            {/* Second row, mirroring Audited: in-place controls on the left, navigate-away pills on
                the right, inside the normal content padding so it lines up with the tables below.
                The design adds View Document / Delete File here later — the structure is ready for
                them; they are not built. */}
            {!isMobile && (
                <div className={styles.ControlsRow}>
                    <div className={styles.ControlsLeft}>
                        {!collapseActions && (
                            <button type="button" className={styles.InlineTextAction} title="Coming soon" disabled>
                                <img src={editClassificationIcon} alt="" width={14} height={14} />
                                Edit Classification
                            </button>
                        )}
                    </div>
                    <div className={styles.ActionsGroup}>
                        {!collapseActions && (
                            <CtaPill label="Financial Ratios" icon={ctaPurpleRight} onClick={() => showFinancialMetrics()} />
                        )}
                        {collapseActions ? (
                            kebabMenu
                        ) : (
                            <>
                                {canUploadData && (
                                    <CtaPill label="Upload Data" icon={UPLOAD_DATA_ICON} onClick={() => setShowUpload(true)} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {isLoading ? (
                <LoadingSpinner color="black" height="40px" style={{ marginTop: '40px' }} />
            ) : (
                <>
                    {statutorySections.map((section) => {
                        const isCashFlowIndirect = section.key === 'cashFlowIndirect';
                        return (
                            <AuditedStatementSection
                                key={section.key}
                                title={
                                    isCashFlowIndirect
                                        ? PROVISIONAL_STATEMENT_FALLBACK_LABELS.cashFlowIndirect
                                        : resolvedMetrics[section.key] || PROVISIONAL_STATEMENT_FALLBACK_LABELS[section.key]
                                }
                                titleInfo={isCashFlowIndirect ? CASH_FLOW_INDIRECT_DISCLOSURE : undefined}
                                layout={section.layout}
                                tableData={section.data}
                                loading={false}
                                unit={unit}
                                inProgress={inProgress}
                                // Provisional's first column reads "Financial Year", not Audited's
                                // "Classification"; ProvisionalOtherMisSection below renders through
                                // RawTable instead of this component, so it never picks this up.
                                firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                                // otherMis renders through ProvisionalOtherMisSection below, which never passes
                                // this prop; the indirect section also omits it (no sourceFilesStatus).
                                referenceDocumentsRow={
                                    isCashFlowIndirect
                                        ? undefined
                                        : { statement: section.key as 'balanceSheet' | 'profitAndLoss' | 'cashFlow', onOpen: viewer.open }
                                }
                            />
                        );
                    })}
                    <ProvisionalOtherMisSection
                        title={resolvedMetrics.otherMis || PROVISIONAL_STATEMENT_FALLBACK_LABELS.otherMis}
                        tableData={otherMisData}
                        loading={false}
                        rawOptions={rawOptions}
                        dataFormatOptions={dataFormatOptions}
                        metric={resolvedMetrics.otherMis}
                        inProgress={inProgress}
                    />
                </>
            )}

            {showUpload && (
                <SideDrawer onClose={() => setShowUpload(false)} width="50%">
                    <UploadData
                        overrideSelectedStep={'financials'}
                        userType={USER_TYPE.INVESTOR}
                        isInvestor
                        assesseeOrgId={orgId}
                        closeDrawer={() => setShowUpload(false)}
                    />
                </SideDrawer>
            )}

            {viewer.viewerProps && <FileViewerDrawer {...viewer.viewerProps} />}
        </div>
    );
};

export default ProvisionalFinancials;
